'use client';

import React from 'react';
import { createBridgeClickHandler, loadToolDraft } from '@/helpers/toolBridge';
import {
  BRACKET_HANDOVER_PARAMS,
  BRACKET_TOOL_SLUG,
  BRACKET_WIDGET_SLUG,
  draftToPayload,
  IBracketDraft,
} from './bracketBridge';
import {
  GENERAL_EXAMPLE_NAMES,
  GENERAL_EXAMPLE_TITLE,
  IBracketVariant,
} from './bracketVariants';
import { StyledCtaBanner } from './BracketTool.styles';

/**
 * What the banner hands over when nobody has typed anything yet.
 *
 * ⚠️ FIXED 2026-09-22, reported by Sergei as "the handover bridge doesn't work".
 * It did work; the banner just had nothing to send. The builder autosaves a
 * draft only once the board stops being our worked example (`isExample` in
 * `BracketTool`), which is right: an example nobody has touched is not the
 * visitor's work and must not come back as "we restored your bracket". But the
 * banner read ONLY that draft, so on an untouched page it handed over an empty
 * payload and the editor opened on "My Bracket / Participant 1..4".
 *
 * That read as broken, and on the event sheets it was close to it: the example
 * draw on the NFL and College Football Playoff pages IS the product, since the
 * seed placeholders are exactly what somebody wants before the field is known.
 *
 * The panel inside the builder never had this gap, because it reads live state.
 * So the two CTAs on one page behaved differently from the same click, which is
 * the actual defect. The banner now falls back to whatever the page is showing,
 * which is what it always claimed to do.
 *
 * Seeds are null here on purpose: the builder's draw order starts at `index`,
 * so an untouched page has no seeding to carry and the panel would send none.
 */
function exampleDraft(variant?: IBracketVariant): IBracketDraft {
  const names = variant ? variant.exampleNames : GENERAL_EXAMPLE_NAMES;

  return {
    title: variant ? variant.exampleTitle : GENERAL_EXAMPLE_TITLE,
    participants: names.map((name) => ({ name, seed: null })),
    format: variant ? variant.defaultFormat : 'single',
    size: variant ? variant.defaultSize : 16,
    thirdPlace: false,
  };
}

interface ICtaBannerProps {
  /**
   * The sheet this page is, when it is one of the eleven variants. Left unset
   * on the general maker, which falls back to its own worked example.
   */
  variant?: IBracketVariant;
}

export const CtaBanner = ({ variant }: ICtaBannerProps) => {
  // This banner sits below the article, out of reach of the builder's own
  // state, so it hands over the draw the builder has been saving as the visitor
  // worked, and the page's own example when there is nothing saved yet.
  const addToWebsite = createBridgeClickHandler({
    toolSlug: BRACKET_TOOL_SLUG,
    widgetSlug: BRACKET_WIDGET_SLUG,
    placement: 'banner',
    getPayload: () =>
      draftToPayload(
        loadToolDraft<IBracketDraft>(BRACKET_TOOL_SLUG) ?? exampleDraft(variant)
      ),
    extraParams: BRACKET_HANDOVER_PARAMS,
  });

  return (
    <StyledCtaBanner>
      <div className="banner-inner">
        <h2>Let the bracket update itself</h2>
        <p>
          Paper is perfect until the results start coming in. Move your draw into the Bracket widget
          and winners advance on their own, visitors vote and predict, and a live leaderboard tracks
          who is ahead. Embed it once and every page it sits on stays current.
        </p>
        <a className="banner-cta" href="/brackets/editor/content" onClick={addToWebsite}>
          Put it on my website &rarr;
        </a>
      </div>
    </StyledCtaBanner>
  );
};
